import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { RenderFlowNotice, RenderFlowStep, RenderJob } from '../render-flow.model';
import { RenderFlowService } from '../render-flow.service';
import { ensureRenderFontsLoaded } from '../render-fonts';
import { goRenderHome, RENDER_LANDING_URL } from '../render-home';
import { RenderNews, RenderNotifyService } from '../render-notify.service';
import { holdPageScrollbar } from '../render-scroll';
import { RENDER_PREVIEW_FINALE_MS } from '../render.data';

type ShellStep = 'upload' | 'library' | RenderFlowStep;

interface Crumb {
  label: string;
  number: number;
}

// A run that ended on a model you are not looking at: in the window's corner, with the way to it.
interface Toast {
  modelId: string;
  name: string;
  text: string;
  action: string;
  url: string;
  failed: boolean;
}

// A ready result goes by itself; a failure stays until it is dismissed or opened (owner,
// 2026-10-01: a tester must see what went wrong). Three at most, newest on top.
const TOAST_READY_MS = 8000;
const TOAST_LIMIT = 3;

// Frame around every flow step: header, the three-step stepper (Upload → Preview · Purchase →
// Download, the designer's 2026-09-23 frames 28547:27443 / 28547:27273), the one-shot notice, the
// step itself; and around "Your library", which has no stepper. It is also the only thing that
// moves the router along: it watches the job in the URL and, whenever the job's step changes
// (a preview finished, a payment went through), navigates to that step's URL. A run that ends on
// another model gets a toast, and the tab's title follows the work (RenderNotifyService).
@Component({
  selector: 'app-render-flow-shell',
  templateUrl: './render-flow-shell.component.html',
  styleUrls: ['./render-flow-shell.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class RenderFlowShellComponent implements OnInit, OnDestroy {
  public readonly crumbs: ReadonlyArray<Crumb> = [
    { label: 'Upload', number: 1 },
    { label: 'Preview · Purchase', number: 2 },
    { label: 'Download', number: 3 },
  ];
  // A sample's walk-through sells nothing (owner, 2026-10-23): its step 2 is the preview alone.
  private readonly sampleCrumbs: ReadonlyArray<Crumb> = this.crumbs.map((crumb) => (crumb.number === 2 ? { ...crumb, label: 'Preview' } : crumb));
  private sampleOnScreen = false;

  public currentStep: ShellStep = 'upload';
  public currentCrumb = 1;
  public modelId: string | null = null;
  public notice: RenderFlowNotice | null = null;
  public toasts: Toast[] = [];

  private routerSubscription?: Subscription;
  private jobSubscription?: Subscription;
  private newsSubscription?: Subscription;
  private releasePageScrollbar?: () => void;
  private releaseTabTitle?: () => void;
  private readonly toastTimers = new Map<string, ReturnType<typeof setTimeout>>();
  // A preview just in, on its processing page: the step after the card's finale.
  private finaleTimer?: ReturnType<typeof setTimeout>;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly flow: RenderFlowService,
    private readonly notify: RenderNotifyService,
    @Inject(DOCUMENT) private readonly document: Document,
  ) {}

  ngOnInit(): void {
    ensureRenderFontsLoaded(this.document);
    // A render that ends while any page of the flow is open gets its browser notification.
    this.notify.watch();
    this.newsSubscription = this.notify.news$.subscribe((news) => this.onNews(news));
    this.releaseTabTitle = this.notify.holdTabTitle();
    this.releasePageScrollbar = holdPageScrollbar(this.document);
    this.syncFromRoute();
    this.routerSubscription = this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => this.syncFromRoute());
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
    this.jobSubscription?.unsubscribe();
    this.newsSubscription?.unsubscribe();
    this.toastTimers.forEach((timer) => clearTimeout(timer));
    clearTimeout(this.finaleTimer);
    this.releaseTabTitle?.();
    this.releasePageScrollbar?.();
    this.flow.setViewing(null);
  }

  public dismissNotice(): void {
    this.notice = null;
  }

  public dismissToast(modelId: string): void {
    clearTimeout(this.toastTimers.get(modelId));
    this.toastTimers.delete(modelId);
    this.toasts = this.toasts.filter((toast) => toast.modelId !== modelId);
  }

  public trackToast(_: number, toast: Toast): string {
    return toast.modelId;
  }

  public get shownCrumbs(): ReadonlyArray<Crumb> {
    return this.sampleOnScreen ? this.sampleCrumbs : this.crumbs;
  }

  // "Your library" is a page of its own, not a step.
  public get isLibrary(): boolean {
    return this.currentStep === 'library';
  }

  // --- stepper: the chevron and the steps behind you ---

  // Back to the landing, on step 1 only (designer + owner, 2026-09-23). Further on, the way back is
  // the steps behind you and the page's own links; a lone "back" there asked where "forward" was,
  // and moving on always takes an action (a model, a payment) that the page's main button does.
  public get canGoBack(): boolean {
    return this.currentStep === 'upload';
  }

  public goBack(): void {
    goRenderHome(this.router);
  }

  // The landing (render-home.ts).
  public readonly homeUrl = RENDER_LANDING_URL;

  public goHome(event: MouseEvent): void {
    if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      return;
    }
    event.preventDefault();
    goRenderHome(this.router);
  }

  // Upload, from anywhere you can leave: nothing while a payment is confirming or the videos are
  // rendering. The way from the checkout back to its preview is the checkout's own link. Leaving
  // never cancels: a preview in progress goes on, in the library and the header; only the step's
  // own "Cancel" deletes the model.
  public canReturnTo(crumb: number): boolean {
    return crumb === 1 && this.currentCrumb > 1 && this.canLeave;
  }

  public returnTo(crumb: number): void {
    if (!this.canReturnTo(crumb)) {
      return;
    }
    this.router.navigateByUrl('/render/upload');
  }

  private get canLeave(): boolean {
    return this.currentStep !== 'confirming' && this.currentStep !== 'rendering';
  }

  private syncFromRoute(): void {
    const child = this.route.firstChild?.snapshot;
    const path = child?.routeConfig?.path?.split('/')[0] || 'upload';
    const modelId = child?.paramMap.get('modelId') || null;

    this.currentStep = path as ShellStep;
    this.currentCrumb = RenderFlowShellComponent.crumbFor(this.currentStep);
    this.notice = this.flow.takeNotice();
    if (modelId) {
      this.dismissToast(modelId);
    }
    window.scrollTo({ top: 0 });

    if (modelId !== this.modelId) {
      this.modelId = modelId;
      this.watchJob();
    }
    this.flow.setViewing(modelId);
  }

  // The model on screen shows its own result; the toast is for the others.
  private onNews(news: RenderNews): void {
    const { job } = news;
    if (!job.unseen) {
      return;
    }
    const text = news.failed
      ? news.what === 'videos' ? 'The videos stopped.' : 'The preview failed.'
      : news.what === 'videos' ? 'Both videos are ready.' : 'The preview is ready.';
    const action = news.failed ? 'See why' : news.what === 'videos' ? 'Download' : 'View';
    const toast: Toast = { modelId: job.modelId, name: job.name, text, action, url: RenderFlowService.stepUrl(job), failed: news.failed };
    this.dismissToast(job.modelId);
    this.toasts = [toast, ...this.toasts].slice(0, TOAST_LIMIT);
    if (!news.failed) {
      this.toastTimers.set(job.modelId, setTimeout(() => this.dismissToast(job.modelId), TOAST_READY_MS));
    }
  }

  private watchJob(): void {
    this.jobSubscription?.unsubscribe();
    this.jobSubscription = undefined;
    this.sampleOnScreen = false;
    clearTimeout(this.finaleTimer);
    this.finaleTimer = undefined;
    if (!this.modelId) {
      return;
    }

    this.jobSubscription = this.flow.job$(this.modelId).subscribe((job?: RenderJob) => {
      this.sampleOnScreen = job?.source.kind === 'sample';
      if (!job) {
        // Cancelled / reset: nothing to show for this id any more.
        this.router.navigateByUrl('/render/upload');
        return;
      }
      if (job.step === this.currentStep) {
        return;
      }
      // A preview that came in while its card was on screen: the card plays its finale first (the
      // bar to its end, the last line ticked), then the preview step develops the frames.
      if (this.currentStep === 'processing' && job.step === 'preview') {
        this.finaleTimer ??= setTimeout(() => {
          this.finaleTimer = undefined;
          const latest = this.flow.jobs.find((item) => item.modelId === job.modelId);
          if (latest && latest.step !== this.currentStep) {
            this.router.navigateByUrl(RenderFlowService.stepUrl(latest));
          }
        }, RENDER_PREVIEW_FINALE_MS);
        return;
      }
      clearTimeout(this.finaleTimer);
      this.finaleTimer = undefined;
      this.router.navigateByUrl(RenderFlowService.stepUrl(job));
    });
  }

  private static crumbFor(step: ShellStep): number {
    switch (step) {
      case 'upload':
        return 1;
      case 'processing':
      case 'preview':
      case 'error':
      case 'checkout':
      case 'confirming':
        return 2;
      case 'rendering':
      case 'downloads':
        return 3;
      default:
        return 1;
    }
  }
}
